<template>
  <MainLayout>
    <PageHeader
      icon="ri-graduation-cap-line"
      title="Finalizar Nivel"
      subtitle="Completa el proceso de finalización de tu nivel académico"
      :show-button="false" />

    <div class="content-container finalizar-nivel-wrapper" ref="top">
      <b-form @submit.stop.prevent="onSubmit">
          <!-- Paso 1: Confirmación de nota -->
          <div v-if="paso == 1" class="step-content">
            <div v-if="form && form.nota_final">
              <p>
                Tu nota acumulada es <b>{{ form.nota_final }}</b>. Esta sería la nota registrada para tu curso de
                {{ form.curso_actual && form.curso_actual.nombre }}. Si deseas continuar selecciona <b>Si</b>, si por el
                contrario deseas solicitar un supletorio (presentar de nuevo un examen) lo puedes hacer en el
                siguiente <a href="https://americanschoolway.edu.co/constancias-y-certificaciones/"
                  target="_blank">enlace</a>.
              </p>
              <p>
                <i>Recuerda que la nota mínima para avanzar al siguiente nivel es <b>3.0</b>.</i>
              </p>
            </div>

            <div v-if="form && form.temas">
              <b-table-simple class="final-grade-table" hover small caption-top responsive>
                <b-thead>
                  <b-th>Tema</b-th>
                  <b-th>Nota</b-th>
                  <b-th>Valor</b-th>
                </b-thead>
                <b-tbody>
                  <b-tr v-for="tema in form.temas" :key="tema.id">
                    <b-td>{{ tema.nombre }}</b-td>
                    <b-td>{{ tema.nota }}</b-td>
                    <b-td>{{ ((((tema.nota / tema.rango_nota) * tema.peso_nota) * 5) / 100).toFixed(3) }}</b-td>
                  </b-tr>
                  <b-tr>
                    <b-td colspan="2"><b>NOTA FINAL</b></b-td>
                    <b-td><b>{{ form.nota_final }}</b></b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>

              <p class="text-center">¿Deseas continuar tu solicitud de finalizar nivel?</p>
              
              <div class="button-group">
                <b-btn variant="outline-primary" class="btn-cancel-secondary" size="sm" @click="continuar(1)">
                  <i class="ri-close-line"></i>
                  Cancelar
                </b-btn>
                <b-btn variant="primary" class="btn-continue-primary" size="sm" @click="continuar(2)">
                  <i class="ri-check-line"></i>
                  Continuar
                </b-btn>
              </div>
            </div>
          </div>

          <!-- Paso 2: Confirmación final -->
          <div v-if="paso == 2" class="step-content step-confirmation">
            <div v-if="form">
              <p v-if="form.nota_final < 3">
                <b>Perdiste el nivel</b> {{ form.curso_actual.nombre }}. La nota mínima para avanzar es de 3.0. <br><br>
                Si continuas el proceso tu nota ({{ form.nota_final }}), quedará registrada en el sistema y deberás
                realizar proceso de nivelación o modificación de niveles, para poder avanzar. <br>
              </p>
              <p v-else>
                Al continuar, tu nota ({{ form.nota_final }}) quedará registrada en nuestro sistema y no podrá ser
                modificada.
              </p>
            </div>

            <div v-if="form && form.temas">
              <p class="text-center">¿Deseas continuar tu solicitud de finalizar nivel?</p>
              
              <div class="button-group">
                <b-btn variant="outline-primary" class="btn-cancel-secondary" size="sm" @click="continuar(1)">
                  <i class="ri-close-line"></i>
                  Cancelar
                </b-btn>
                <b-btn variant="primary" class="btn-continue-primary" size="sm" @click="continuar(3)">
                  <i class="ri-check-line"></i>
                  Continuar
                </b-btn>
              </div>
            </div>
          </div>

          <!-- Paso 3: Actualizar datos -->
          <div v-else-if="paso == 3" class="step-content">
            

            <p class="intro-text">
              <strong>{{ user.data.primer_nombre }},</strong> para continuar con tu solicitud de finalizar nivel es necesario confirmar y/o actualizar los siguientes datos. De esta manera mantendremos tu información actualizada en nuestra base académica y en la secretaria de educación:
            </p>

            <p class="minor-note">
              <strong>NOTA:</strong> si eres estudiante menor de edad, deberás diligenciar la información bajo la supervisión de tu acudiente o representante legal.
            </p>

            <!-- Datos de solo lectura - Layout compacto -->
            <div class="readonly-info-grid">
              <div class="readonly-item">
                <strong>Nombres:</strong>
                <span>{{ user.data.primer_nombre }} {{ user.data.segundo_nombre }}</span>
              </div>
              <div class="readonly-item">
                <strong>Apellidos:</strong>
                <span>{{ user.data.primer_apellido }} {{ user.data.segundo_apellido }}</span>
              </div>
              <div class="readonly-item">
                <strong>Correo:</strong>
                <span>{{ user.data.email }}</span>
              </div>
              <div class="readonly-item">
                <strong>N° Documento:</strong>
                <span>{{ user.data.numero_documento }}</span>
              </div>
            </div>

            <!-- Campos editables - Layout de 2 columnas -->
            <div class="form-fields-grid">
              <!-- Fila 1: Tipo documento + Estado civil -->
              <div class="form-field-compact">
                <label for="tipo_documento" class="form-field-label">Tipo documento:</label>
                <div class="form-field-input">
                  <b-form-select id="tipo_documento" v-model="$v.form.tipo_documento.$model"
                    :state="validateState('tipo_documento')" aria-describedby="tipo_documento-live-feedback">
                    <b-form-select-option disabled selected value="">Por favor, seleccione</b-form-select-option>
                    <b-form-select-option v-for="td in tipos_documentos" :key="td.codigo"
                      :value="td.codigo">{{ td.descripcion }}</b-form-select-option>
                  </b-form-select>
                  <b-form-invalid-feedback v-if="$v.form.tipo_documento.$error" id="tipo_documento-live-feedback">Este es un campo obligatorio.</b-form-invalid-feedback>
                </div>
              </div>

              <div class="form-field-compact">
                <label for="estado_civil_id" class="form-field-label">Estado civil:</label>
                <div class="form-field-input">
                  <b-form-select id="estado_civil_id" v-model="$v.form.estado_civil_id.$model"
                    :state="validateState('estado_civil_id')" aria-describedby="estado_civil_id-live-feedback">
                    <b-form-select-option disabled value="" selected>Por favor, seleccione</b-form-select-option>
                    <b-form-select-option v-for="item in estados_civiles" :key="item.id"
                      :value="item.id">{{ etiquetaOpcion(item.descripcion) }}</b-form-select-option>
                  </b-form-select>
                  <b-form-invalid-feedback v-if="$v.form.estado_civil_id.$error" id="estado_civil_id-live-feedback">Este es un campo obligatorio.</b-form-invalid-feedback>
                </div>
              </div>

              <!-- Fila 2: Fecha nacimiento + Género -->
              <div class="form-field-compact">
                <label for="fecha_nacimiento" class="form-field-label">Fecha de nacimiento:</label>
                <div class="form-field-input">
                  <b-form-input id="fecha_nacimiento" type="date" v-model="$v.form.fecha_nacimiento.$model"
                    :state="validateState('fecha_nacimiento')"></b-form-input>
                  <b-form-invalid-feedback v-if="$v.form.fecha_nacimiento.$error" id="fecha_nacimiento-live-feedback">Por favor ingrese una fecha válida.</b-form-invalid-feedback>
                </div>
              </div>

              <div class="form-field-compact">
                <label for="genero" class="form-field-label">Género:</label>
                <div class="form-field-input">
                  <b-form-select id="genero" v-model="$v.form.genero.$model"
                    :state="validateState('genero')" aria-describedby="genero-live-feedback">
                    <b-form-select-option disabled selected value="">Por favor, seleccione</b-form-select-option>
                    <b-form-select-option v-for="item in GENEROS" :key="item"
                      :value="item">{{ etiquetaOpcion(item) }}</b-form-select-option>
                  </b-form-select>
                  <b-form-invalid-feedback v-if="$v.form.genero.$error" id="genero-live-feedback">Este es un campo obligatorio.</b-form-invalid-feedback>
                </div>
              </div>

              <!-- Fila 3: Departamento + Lugar de origen -->
              <div class="form-field-compact">
                <label for="departamento_origen" class="form-field-label">Departamento:</label>
                <div class="form-field-input">
                  <vue-typeahead-bootstrap :serializer="s => s.nombre" @hit="hitState($event)" :data="states" v-model="$v.form.departamento_origen.$model" :state="validateState('departamento_origen')" />
                  <b-form-invalid-feedback v-if="$v.form.departamento_origen.$dirty && $v.form.departamento_origen.$invalid" id="departamento_origen-live-feedback">Este es un campo obligatorio.</b-form-invalid-feedback>
                </div>
              </div>

              <div class="form-field-compact">
                <label for="lugar_origen" class="form-field-label">Lugar de origen:</label>
                <div class="form-field-input">
                    <vue-typeahead-bootstrap id="lugar_origen" :serializer="serializarCiudad" @hit="seleccionarCiudad" :data="cities" v-model="ciudadOrigenSearch" :state="validateState('lugar_origen')" />
                  <b-form-invalid-feedback v-if="$v.form.lugar_origen.$dirty && $v.form.lugar_origen.$invalid" id="lugar_origen-live-feedback">Este es un campo obligatorio.</b-form-invalid-feedback>
                </div>
              </div>

              <!-- Fila 4: Estrato + Régimen de salud -->
              <div class="form-field-compact">
                <label for="estrato" class="form-field-label">Estrato:</label>
                <div class="form-field-input">
                  <b-form-select id="estrato" v-model="$v.form.estrato.$model"
                    :state="validateState('estrato')" aria-describedby="estrato-live-feedback">
                    <b-form-select-option disabled selected value="">Por favor, seleccione</b-form-select-option>
                    <b-form-select-option v-for="item in ESTRATOS" :key="item"
                      :value="item">{{ etiquetaOpcion(item) }}</b-form-select-option>
                  </b-form-select>
                  <b-form-invalid-feedback v-if="$v.form.estrato.$error" id="estrato-live-feedback">Este es un campo obligatorio.</b-form-invalid-feedback>
                </div>
              </div>

              <div class="form-field-compact">
                <label for="regimen_salud_id" class="form-field-label">Régimen de salud:</label>
                <div class="form-field-input">
                  <b-form-select id="regimen_salud_id" v-model="$v.form.regimen_salud_id.$model"
                    :state="validateState('regimen_salud_id')" aria-describedby="regimen_salud_id-live-feedback">
                    <b-form-select-option disabled selected value="">Por favor, seleccione</b-form-select-option>
                    <b-form-select-option v-for="item in lista_regimen_salud" :key="item.id"
                      :value="item.id">{{ etiquetaOpcion(item.nombre) }}</b-form-select-option>
                  </b-form-select>
                  <b-form-invalid-feedback v-if="$v.form.regimen_salud_id.$error" id="regimen_salud_id-live-feedback">Este es un campo obligatorio.</b-form-invalid-feedback>
                </div>
              </div>

              <!-- Fila 5: Nivel de formación + Ocupación -->
              <div class="form-field-compact">
                <label for="nivel_educativo_id" class="form-field-label">Nivel de formación:</label>
                <div class="form-field-input">
                  <b-form-select id="nivel_educativo_id" v-model="$v.form.nivel_educativo_id.$model"
                    :state="validateState('nivel_educativo_id')" aria-describedby="nivel_educativo_id-live-feedback">
                    <b-form-select-option disabled selected value="">Por favor, seleccione</b-form-select-option>
                    <b-form-select-option v-for="item in lista_nivel_formacion" :key="item.id"
                      :value="item.id">{{ etiquetaOpcion(item.descripcion) }}</b-form-select-option>
                  </b-form-select>
                  <b-form-invalid-feedback v-if="$v.form.nivel_educativo_id.$error" id="nivel_educativo_id-live-feedback">Este es un campo obligatorio.</b-form-invalid-feedback>
                </div>
              </div>

              <div class="form-field-compact">
                <label for="ocupacion_id" class="form-field-label">Ocupación:</label>
                <div class="form-field-input">

                  <b-form-select id="ocupacion_id" v-model="$v.form.ocupacion_id.$model"
                    :state="validateState('ocupacion_id')" aria-describedby="ocupacion_id-live-feedback">
                    <b-form-select-option disabled selected value="">Por favor, seleccione</b-form-select-option>
                    <b-form-select-option v-for="item in ocupaciones" :key="item.id"
                      :value="item.id">{{ etiquetaOpcion(item.descripcion) }}</b-form-select-option>
                  </b-form-select>

                  <b-form-invalid-feedback v-if="$v.form.ocupacion_id.$error" id="ocupacion_id-live-feedback">Este es un campo obligatorio.</b-form-invalid-feedback>
                </div>
              </div>

              <!-- Fila 6: Discapacidad y etnia -->
              <div class="form-field-compact">
                <label for="discapacidad_id" class="form-field-label">Discapacidad:</label>
                <div class="form-field-input">
                  <b-form-select id="discapacidad_id" v-model="$v.form.discapacidad_id.$model"
                    :state="validateState('discapacidad_id')" aria-describedby="discapacidad_id-live-feedback">
                    <b-form-select-option disabled selected value="">Por favor, seleccione</b-form-select-option>
                    <b-form-select-option v-for="item in discapacidades" :key="item.id"
                      :value="item.id">{{ etiquetaOpcion(item.descripcion) }}</b-form-select-option>
                  </b-form-select>
                  <b-form-invalid-feedback v-if="$v.form.discapacidad_id.$error" id="discapacidad_id-live-feedback">Este es un campo obligatorio.</b-form-invalid-feedback>
                </div>
              </div>

              <!-- Grupo étnico -->
              <div class="form-field-compact">
                <label for="grupo_etnico_id" class="form-field-label">Seleccione si pertenece a alguno de los siguientes grupos:</label>
                <div class="form-field-input">
                  <b-form-select id="grupo_etnico_id" v-model="$v.form.grupo_etnico_id.$model"
                    :state="validateState('grupo_etnico_id')" aria-describedby="grupo_etnico_id-live-feedback">
                    <b-form-select-option disabled selected value="">Por favor, seleccione</b-form-select-option>
                    <b-form-select-option v-for="item in lista_grupo_etnico" :key="item.id"
                      :value="item.id">{{ etiquetaOpcion(item.descripcion) }}</b-form-select-option>
                  </b-form-select>
                  <b-form-invalid-feedback v-if="$v.form.grupo_etnico_id.$error" id="grupo_etnico_id-live-feedback">Este es un campo obligatorio.</b-form-invalid-feedback>
                </div>
              </div>
              
            </div>

            <div class="button-group centered">
              <b-btn variant="primary" class="btn-continue-primary" size="sm" type="submit">
                <i class="ri-save-line"></i>
                Guardar
              </b-btn>
            </div>
          </div>

          <!-- Paso 4: Éxito -->
          <div v-else-if="paso == 4" class="step-content step-success">
            <p class="text-center">
              <b>Proceso Exitoso</b>
            </p>
            <p>
              {{ user.data.primer_nombre }}, <br><br>
              <span v-html="mensaje"></span>
            </p>
            <div class="button-group centered">
              <b-button class="btn-continue-primary" :to="{ name: 'avanzar-nivel' }" variant="primary">
                <i class="ri-arrow-right-line"></i>
                Continuar
              </b-button>
            </div>
          </div>

          <!-- Paso 5: Advertencia de ausencias -->
          <div v-if="paso == 5" class="step-content step-warning">
            <div v-if="form && form.nota_final">
              <p>
                <b>Has perdido el nivel por superar el límite de inasistencias</b> La nota de finalización es 0
                (cero). Si deseas continuar selecciona Si, si por el contrario deseas recuperar las clases perdidas,
                debes solicitar los supletorios correspondientes (ver las clases no asistidas) en el siguiente link:
                <a href="https://americanschoolway.edu.co/solicitudes-academicas/" target="_blank">click aquí</a>.
              </p>
            </div>

            <p class="text-center">¿Deseas continuar tu solicitud de finalizar nivel?</p>

            <div class="button-group">
              <b-btn variant="outline-primary" class="btn-cancel-secondary" size="sm" @click="continuar(1)">
                <i class="ri-close-line"></i>
                Cancelar
              </b-btn>
              <b-btn variant="primary" class="btn-continue-primary" size="sm" @click="continuar(6)">
                <i class="ri-check-line"></i>
                Continuar
              </b-btn>
            </div>
          </div>

          <!-- Paso 6: Confirmación nota 0.0 -->
          <div v-if="paso == 6" class="step-content step-confirmation">
            <div v-if="form && form.nota_final">
              <p>
                Al continuar tu nota 0,0 quedará registrada en nuestro sistema y no podrá ser modificada.
              </p>
              <p>
                <i>Recuerda que la nota mínima para avanzar al siguiente nivel es <b>3.0</b>.</i>
              </p>
            </div>

            <p class="text-center">¿Deseas continuar tu solicitud de finalizar nivel?</p>

            <div class="button-group">
              <b-btn variant="outline-primary" class="btn-cancel-secondary" size="sm" @click="continuar(1)">
                <i class="ri-close-line"></i>
                Cancelar
              </b-btn>
              <b-btn variant="primary" class="btn-continue-primary" size="sm" @click="continuar(3)">
                <i class="ri-check-line"></i>
                Continuar
              </b-btn>
            </div>
          </div>

      </b-form>
    </div>

    <!-- Loader Moderno -->
    <ModernLoader
      :show="informationLoading"
      message="Cargando información..."
      description="Por favor espera mientras procesamos tu solicitud" />
  </MainLayout>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';

import { validationMixin } from 'vuelidate'
import { required, minLength, maxLength, numeric, regex } from "vuelidate/lib/validators";

import VueTypeaheadBootstrap from 'vue-typeahead-bootstrap';

import MainLayout from '@/components/Layout/MainLayout.vue';
import PageHeader from '@/components/Layout/PageHeader.vue';
import ModernLoader from '@/components/Sections/ModernLoader.vue';

export default {
  components: {
    MainLayout,
    PageHeader,
    ModernLoader,
    VueTypeaheadBootstrap,
  },
  mixins: [validationMixin],
  name: 'FinalizarNivelPage',
  data() {
    return {
      ESTRATOS: ['1) 1','2) 2','3) 3','4) 4','5) 5','6) 6', '99) SIN INFORMACIÓN'],
      GENEROS: ['1) MASCULINO','2) FEMENINO', '3) NO BINARIO', '4) TRANSEXUAL', '5) NO INFORMA'],
      formError: [],
      form: {
        id: null,
        tipo_documento: null,
        numero_documento: null,
        nota_final: null,
        nota_ife: null,
        curso_actual: null,
        estado_civil_id: null,
        estrato: null,
        fecha_nacimiento: null,
        lugar_origen: null,
        departamento_origen: null,
        pais_origen: null,
        lugar_residencia: null,
        genero: null,
        nivel_formacion: null,
        nota_final: null,
        ocupacion: null,
        regimen_salud_id: null,
        ocupacion_id: null,
        discapacidad_id: null,
        grupo_etnico_id: null,
        nivel_educativo_id: null,
        modulos_adquiridos: null,
        fecha_matricula: null,
        idioma: null
      },
      paso: 1,
      mensaje: null,
      ocupacionSearch: null,
      countrySearch: null,
      stateSearch: null,
      citySearch: null,
      ciudadOrigenSearch: null,
      informationLoading: true
    }
  },
  validations: {
    form: {
      tipo_documento: {
        required
      },
      numero_documento: {
        required,
        numeric,
        minLength: minLength(3),
        maxLength: maxLength(20)
      },
      estado_civil_id: {
        required
      },
      fecha_nacimiento: {
        required,
        minValue: value => {
          let minDate = new Date()
          minDate.setFullYear(minDate.getFullYear() - 100)
          const min = minDate.toISOString().split("T")[0];
          return value > min
        },
        maxValue: value => {
          let maxDate = new Date()
          maxDate.setFullYear(maxDate.getFullYear() - 7)
          const max = maxDate.toISOString().split("T")[0];
          return value < max
        }
      },
      lugar_origen: {
        required
      },
      departamento_origen: {
        required
      },
      estrato: {
        required
      },
      regimen_salud_id: {
        required
      },
      nivel_educativo_id: {
        required
      },
      ocupacion_id: {
        required
      },
      discapacidad_id: {
        required
      },
      grupo_etnico_id: {
        required
      },
      genero: {
        required
      },
    }
  },
  mounted() {
    this.traerTemaSiguiente();
    this.fetchTipoDocumento()
    this.fetchEstadoCivil()
    this.fetchRegimenSalud();
    this.fetchNivelFormacion();
    this.fetchOcupaciones().finally(() => {
      this.setOcupacion();
    });
    this.fetchGrupoEtnico();
    this.fetchDiscapacidades();
    this.fetchStates();
    this.getInformation();
  },
  methods: {
    ...mapActions({
      fetchSolicitarAvance: 'academico/fetchSolicitarAvance',
      fetchCursoFinalizado: 'academico/fetchCursoFinalizado',
      fetchDisponibilidad: 'academico/fetchDisponibilidad',
      fetchTipoDocumento: 'tipoDocumento/fetchLista',
      fetchEstadoCivil: 'estadoCivil/fetchLista',
      fetchRegimenSalud: 'regimenSalud/fetchLista',
      fetchNivelFormacion: 'nivelFormacion/fetchLista',
      fetchOcupaciones: 'ocupacion/fetchLista',
      fetchGrupoEtnico: 'grupoEtnico/fetchLista',
      fetchDiscapacidades: 'discapacidad/fetchLista',
      fetchCiudades: 'ciudad/fetchLista',
      fetchInformacion: 'student/informacion',
      fetchStates: 'ciudad/fetchStates',
      fetchCities: 'ciudad/fetchCities',
    }),
    ...mapMutations({
      setShowNotification: 'setShowNotification'
    }),
    getInformation() {
      this.fetchInformacion().then(response => {
        if (!this.form) this.form = {};
        for (var i in response) {
          this.form[i] = response[i];
        }
        this.setOcupacion()
      }).catch(err => {
        console.error(err);
      }).finally(() => {
        this.informationLoading = false;
      })
    },
    traerTemaSiguiente() {
      this.fetchDisponibilidad(false).then((result) => {
        this.$router.push(`/`)
      }).catch((err) => {
        if (err && err.data && err.data.error && ['No tienes más temas pendientes.', 'No tienes más temas pendientes. Recuerda presentar tu test Versant'].includes(err.data.error)) {
          this.cursoFinalizado();
        } else {
          this.$router.push(`/`)
        }
      })
    },
    getCountries() {

    },
    cursoFinalizado() {
      this.fetchCursoFinalizado().then((result) => {
        if (result && result.dato && result.dato.curso_actual && result.dato.curso_actual.estudiante) {
          this.$router.push(`/avanzar-nivel`)
        } else {
          if (result.codigo == 'inasistencia') {
            this.mensaje = result.mensaje;
            this.paso = 5;
          } else {
            this.paso = 1;
          }
        }
      }).catch((err) => {
        console.error(err)
      })
    },
    continuar(tipo) {
      if (tipo == 1) {
        history.back()
      } else {
        this.paso = tipo;
        this.scrollMeTo('top')
      }
    },
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },
    onSubmit() {
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        return;
      }

      this.fetchSolicitarAvance(this.form).then((result) => {

        if (result && result.codigo == 'ok') {
          if (result && result.mensaje) {
            this.mensaje = result.mensaje;
            this.continuar(4);
          }
        }
      }).catch((err) => {
        console.error(err)
      })

    },
    scrollMeTo(refName) {
      var element = this.$refs[refName];
      var top = element.offsetTop;
      window.scrollTo(0, top);
    },

    isLetter(e) {
      let char = String.fromCharCode(e.keyCode); // Get the character
      if (utils.LETTER_REGEX.test(char)) return true; // Match with regex 
      else e.preventDefault(); // If not match, don't add to input text
    },
    isNumber(e) {
      let char = String.fromCharCode(e.keyCode); // Get the character
      if (/^[0-9]+$/.test(char)) return true; // Match with regex 
      else e.preventDefault(); // If not match, don't add to input text
    },
    reviewText(event, field) {
      let char = String.fromCharCode(event.keyCode);
      if (!utils.LETTER_REGEX.test(char)) {
        this.$v.form[field].$model = utils.eliminarCaracteres(this.$v.form[field].$model);
      }
    },
    reviewTextChange(event) {
      if (!utils.LETTER_REGEX.test(this.$v.form[field].$model)) {
        this.$v.form[field].$model = utils.eliminarCaracteres(this.$v.form[field].$model);
      }
    },
    ocupacionSeleccionada(item) {
      
    },
    hitState($event) {
      this.$v.form.departamento_origen.$model = $event.nombre;
      this.fetchCities({ id: $event.id });
    },
    serializarCiudad(ciudad) {
      if (!ciudad) return '';
      return ciudad.nombre || ciudad.descripcion || ciudad.codigo_nombre || '';
    },
    seleccionarCiudad(ciudad) {
      this.$v.form.lugar_origen.$model = ciudad
        ? (ciudad.codigo_nombre || this.serializarCiudad(ciudad))
        : null;
      this.ciudadOrigenSearch = this.serializarCiudad(ciudad);
    },
    etiquetaOpcion(valor) {
      return valor.replace(/^\d+\)\s*/, '');
    },
    setOcupacion() {
      if (this.form && this.form.ocupacion_id) {
        let ocupacion = this.ocupaciones.find(x => x.id == this.form.ocupacion_id);
        if (ocupacion) {
          this.form.ocupacion = ocupacion.descripcion;
          this.ocupacionSearch = this.etiquetaOpcion(ocupacion.descripcion);
        }
      }
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      siguienteTema: state => state.academico.siguienteTema,
      // sedeActual: state => state.academico.sedeActual, 
      // cursoActual: state => state.academico.cursoActual,
      // sedes: state  => state.sedes.lista,
      show_notification: state => state.show_notification,
      tipos_documentos: state => state.tipoDocumento.lista,
      estados_civiles: state => state.estadoCivil.lista,
      lista_regimen_salud: state => state.regimenSalud.lista,
      lista_nivel_formacion: state => state.nivelFormacion.lista,
      ocupaciones: state => state.ocupacion.lista,
      discapacidades: state => state.discapacidad.lista,
      lista_grupo_etnico: state => state.grupoEtnico.lista,
      lista_ciudades: state => state.ciudad.lista,
      siguienteTema: state => state.academico.siguienteTema,
      countries: state => state.ciudad.countries,
      states: state => state.ciudad.states,
      cities: state => state.ciudad.cities,
    }),
    ...mapGetters({
      // getSede: 'sedes/getSede',
    }),
    tipodocumento() {
      if (this.form && this.form.tipo_documento) {
        let item = this.tipos_documentos.find(x => x.codigo == this.form.tipo_documento);
        if (item) {
          return item.descripcion;
        }
      }
      return null;
    },
    minDateFormat() {
      let now = new Date()
      now.setFullYear(now.getFullYear() - 80)
      return this.$moment(now).format("YYYY-MM-DD");
    },
    maxDateFormat() {
      let now = new Date()
      now.setFullYear(now.getFullYear() - 7)
      return this.$moment(now).format("YYYY-MM-DD");

    },
    minDate() {
      let now = new Date()
      now.setFullYear(now.getFullYear() - 80)
      return now;
    },
    maxDate() {
      let now = new Date()
      now.setFullYear(now.getFullYear() - 7)
      return now;
    },
    ciudadesLabel() {
      if (this.lista_ciudades) {
        return this.lista_ciudades.map(x => x.nombre)
      }
      return [];
    },
  },
  watch: {
    ocupacionSearch: function (newVal, oldVal) {
      var item = this.ocupaciones.find(x => this.etiquetaOpcion(x.descripcion) == newVal);
      if (!item) {
        this.$v.form.ocupacion_id.$model = null;
      }
    },
    countrySearch: function (newVal, oldVal) {
      var item = this.countries.find(x => x.country_name == newVal);
      if (!item) {
        this.$v.form.pais_origen.$model = null;
      }
    },
    stateSearch: function (newVal, oldVal) {
      var item = this.states.find(x => x.nombre == newVal);
      if (!item) {
        this.$v.form.departamento_origen.$model = null;
      }
    },
    citySearch: function (newVal, oldVal) {
      var item = this.cities.find(x => x.nombre == newVal);
      if (!item) {
        this.$v.form.lugar_origen.$model = null;
      }
    }
  }
}
</script>

<style scoped>
/* ============================================================================
   ANIMACIÓN DE ENTRADA
   ============================================================================ */
@keyframes fadeSlideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.step-content {
  animation: fadeSlideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Reducir padding en paso 3 (formulario) */
.step-content:has(.form-fields-grid) {
  padding: 0.5rem 0;
}

/* ============================================================================
   DATOS DE SOLO LECTURA (READONLY)
   ============================================================================ */
.readonly-info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem 1.5rem;
  background: var(--theme-bg-tertiary);
  border-radius: 12px;
  padding: 0.75rem 1rem;
  border: 1px solid var(--theme-border-color-light);
  margin-bottom: 1.25rem;
}

.readonly-item {
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
}

.readonly-item strong {
  color: var(--theme-text-muted);
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  white-space: nowrap;
  flex-shrink: 0;
}

.readonly-item span {
  color: var(--theme-text-primary);
  font-size: 0.95rem;
  font-weight: 500;
  word-break: break-word;
  flex: 1;
  min-width: 0;
}

/* ============================================================================
   FORMULARIO - GRID DE 2 COLUMNAS
   ============================================================================ */
.form-fields-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.875rem 1.5rem;
}

.form-field-compact {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.form-field-compact .form-field-label {
  color: var(--theme-text-secondary);
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 0;
}

.form-field-compact.full-width {
  grid-column: 1 / -1;
}

/* ============================================================================
   GRUPOS DE BOTONES
   ============================================================================ */
.button-group {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1.5rem;
  padding-top: 1rem;
}

.button-group.centered {
  margin-top: 1.25rem;
  padding-top: 0.5rem;
}

.minor-note {
  margin: 1.25rem 0;
  padding: 0.75rem 1rem;
  background: var(--theme-hover-bg-light);
  border-left: 3px solid var(--app-primary-blue);
  border-radius: 6px;
  color: var(--theme-text-secondary);
  font-size: 0.8rem;
  line-height: 1.5;
}

.minor-note strong {
  color: var(--theme-text-primary);
}

.button-group .btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-width: 140px;
  min-height: 42px;
  padding: 0.625rem 1.25rem;
  border: 1px solid transparent;
  border-radius: 10px;
  box-shadow: var(--theme-shadow-sm);
  font-size: 0.875rem;
  font-weight: 600;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.button-group .btn i {
  font-size: 1rem;
}

.button-group .btn-success {
  background: var(--app-color-green);
  border-color: var(--app-color-green);
  color: var(--app-header-text);
}

.button-group .btn-danger {
  background: var(--app-primary-red);
  border-color: var(--app-primary-red);
  color: var(--app-header-text);
}

.button-group .btn:hover,
.button-group .btn:focus {
  color: var(--app-header-text);
  transform: translateY(-2px);
  box-shadow: var(--theme-shadow-md);
}

.button-group .btn:focus {
  outline: 2px solid var(--app-primary-blue);
  outline-offset: 2px;
}

.button-group .btn-cancel-secondary,
.button-group .btn-cancel-secondary:hover,
.button-group .btn-cancel-secondary:focus {
  background: var(--theme-bg-tertiary);
  border-color: var(--app-primary-blue);
  color: var(--theme-text-primary);
}

.button-group .btn-cancel-secondary:hover {
  background: var(--theme-bg-secondary);
}

.btn-continue-primary,
.button-group .btn-continue-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: var(--theme-gradient-primary);
  border: none;
  border-radius: 10px;
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: var(--theme-shadow-sm);
}

.btn-continue-primary:hover,
.btn-continue-primary:focus,
.button-group .btn-continue-primary:hover,
.button-group .btn-continue-primary:focus {
  background: var(--theme-gradient-primary);
  color: white;
  transform: translateY(-2px);
  box-shadow: var(--theme-shadow-md);
}

/* ============================================================================
   TABLA DE NOTAS
   ============================================================================ */
.final-grade-table {
  overflow: hidden;
  margin-bottom: 1.5rem;
  background: var(--theme-bg-card);
  border: 1px solid var(--theme-border-color);
  border-radius: 10px;
  color: var(--theme-text-primary);
}

.final-grade-table table {
  margin-bottom: 0;
  color: var(--theme-text-primary);
}

.final-grade-table thead {
  background: var(--app-primary-blue);
}

.final-grade-table thead th {
  border-color: var(--app-primary-blue);
  color: var(--app-header-text);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.final-grade-table thead th:first-child,
.final-grade-table tbody td:first-child {
  padding-left: 1.25rem;
}

.final-grade-table thead th:nth-child(2),
.final-grade-table thead th:nth-child(3),
.final-grade-table tbody td:nth-child(2),
.final-grade-table tbody td:nth-child(3) {
  text-align: right;
}

.final-grade-table thead th:last-child,
.final-grade-table tbody td:last-child {
  padding-right: 1.25rem;
}

.final-grade-table tbody td {
  background: var(--theme-bg-card);
  border-color: var(--theme-border-color);
  color: var(--theme-text-primary);
}

.final-grade-table tbody tr:hover td {
  background: var(--theme-bg-card-hover);
  color: var(--theme-text-primary);
}

.final-grade-table tbody tr:last-child td {
  font-weight: 600;
  background: var(--theme-bg-secondary);
  color: var(--theme-text-primary);
}

/* ============================================================================
   TEXTO INTRODUCTORIO
   ============================================================================ */
.intro-text {
  margin-bottom: 1rem;
  line-height: 1.5;
  font-size: 0.9rem;
}

.intro-text strong {
  color: var(--theme-text-primary);
  font-weight: 600;
}

/* ============================================================================
   RESPONSIVE
   ============================================================================ */
@media (max-width: 1024px) {
  .readonly-info-grid {
    gap: 0.5rem 1rem;
    padding: 0.625rem 0.875rem;
  }

  .form-fields-grid {
    gap: 0.875rem 1.25rem;
  }
}

@media (max-width: 768px) {
  .readonly-info-grid,
  .form-fields-grid {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    padding: 0.625rem 0.75rem;
  }

  .readonly-item {
    gap: 0.375rem;
  }

  .readonly-item strong {
    font-size: 0.75rem;
  }

  .readonly-item span {
    font-size: 0.875rem;
  }

  .form-field-compact {
    gap: 0.25rem;
  }

  .form-field-compact .form-field-label {
    font-size: 0.85rem;
  }

  .form-field-compact.full-width {
    grid-column: 1;
  }

  .button-group {
    flex-direction: column;
    gap: 0.75rem;
  }

  .button-group .btn {
    width: 100%;
    min-width: unset;
  }
}

@media (max-width: 480px) {
  .readonly-info-grid {
    gap: 0.375rem;
    padding: 0.5rem 0.625rem;
  }

  .readonly-item {
    gap: 0.25rem;
  }

  .readonly-item strong {
    font-size: 0.7rem;
  }

  .readonly-item span {
    font-size: 0.8rem;
  }

  .form-fields-grid {
    gap: 0.625rem;
  }

  .form-field-compact .form-field-label {
    font-size: 0.8rem;
  }

  .button-group {
    gap: 0.625rem;
    margin-top: 1rem;
  }

  .step-content {
    padding: 0.5rem 0;
  }
}
</style>